import { ChevronDown, ChevronRight } from 'lucide-react'
import { Fragment, type ReactNode, useState } from 'react'

import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { TableCardList } from '@/components/ui/table-card'
import { cn } from '@/lib/utils'
import { type DocumentGroup, documentTypeLabel, useOrgDocumentsByType } from '@/models/document'
import { DocumentCards } from './DocumentCards'
import { COMPACT_ROW, DocumentRows } from './DocumentRow'
import { DOCUMENTS_TABLE_HEADER, NO_DOCUMENTS } from './DocumentsTable'
import { type DocumentRowActions, useDocumentRowActions } from './useDocumentRowActions'

function GroupHeaderRow({
  group,
  chevron,
  onClick,
}: {
  group: DocumentGroup
  chevron?: ReactNode
  onClick?: () => void
}) {
  return (
    <TableRow className={cn('bg-muted/50', COMPACT_ROW, onClick && 'cursor-pointer')} onClick={onClick}>
      <TableCell colSpan={7} className="font-semibold">
        <span className="flex items-center gap-2">
          {chevron}
          {documentTypeLabel(group.documentType)} ({group.count})
        </span>
      </TableCell>
    </TableRow>
  )
}

/** One type collapsed behind a "Type (N)" row — its documents are fetched lazily, only once expanded. */
function CollapsedGroupRows({
  orgId,
  group,
  openDetails,
  openRename,
  openPreview,
}: { orgId: string; group: DocumentGroup } & DocumentRowActions) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { documents, isLoading } = useOrgDocumentsByType(isExpanded ? orgId : undefined, group.documentType)

  return (
    <>
      <GroupHeaderRow
        group={group}
        onClick={() => setIsExpanded((expanded) => !expanded)}
        chevron={
          isExpanded ? (
            <ChevronDown className="text-muted-foreground size-4" />
          ) : (
            <ChevronRight className="text-muted-foreground size-4" />
          )
        }
      />
      {isExpanded && isLoading && (
        <TableRow>
          <TableCell colSpan={7}>
            <SkeletonRows rows={3} rowClassName="h-6" />
          </TableCell>
        </TableRow>
      )}
      {isExpanded && !isLoading && (
        <DocumentRows
          documents={documents}
          openDetails={openDetails}
          openRename={openRename}
          openPreview={openPreview}
        />
      )}
    </>
  )
}

/** Card-stack counterpart of `CollapsedGroupRows` — the count row becomes a tappable bar. */
function CollapsedGroupCards({
  orgId,
  group,
  openDetails,
  openRename,
  openPreview,
}: { orgId: string; group: DocumentGroup } & DocumentRowActions) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { documents, isLoading } = useOrgDocumentsByType(isExpanded ? orgId : undefined, group.documentType)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-auto min-h-10 justify-start text-left font-semibold whitespace-normal"
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        {isExpanded ? <ChevronDown /> : <ChevronRight />}
        {documentTypeLabel(group.documentType)} ({group.count})
      </Button>
      {isExpanded && isLoading && <SkeletonRows rows={3} rowClassName="h-6" />}
      {isExpanded && !isLoading && (
        <DocumentCards
          documents={documents}
          openDetails={openDetails}
          openRename={openRename}
          openPreview={openPreview}
        />
      )}
    </>
  )
}

/** Documents grouped by type: a small type's documents render inline, a large type collapses behind a count row that lazily expands. Staff-only, so files open in the preview. */
export function DocumentGroupsTable({ groups, orgId }: { groups: DocumentGroup[]; orgId: string }) {
  const { openDetails, openRename, openPreview, dialogs } = useDocumentRowActions()

  if (groups.length === 0) return NO_DOCUMENTS

  // Groups arrive most-recently-active first — render each in that position rather than sorting inline groups ahead of collapsed ones.
  return (
    <>
      <TableCardList className="xl:hidden">
        {groups.map((group) =>
          group.documents.length > 0 ? (
            <DocumentCards
              key={group.documentType}
              documents={group.documents}
              openDetails={openDetails}
              openRename={openRename}
              openPreview={openPreview}
            />
          ) : (
            <CollapsedGroupCards
              key={group.documentType}
              orgId={orgId}
              group={group}
              openDetails={openDetails}
              openRename={openRename}
              openPreview={openPreview}
            />
          )
        )}
      </TableCardList>

      <div className="hidden xl:block">
        <Table>
          {DOCUMENTS_TABLE_HEADER}
          <TableBody>
            {groups.map((group) =>
              group.documents.length > 0 ? (
                <Fragment key={group.documentType}>
                  <GroupHeaderRow group={group} />
                  <DocumentRows
                    documents={group.documents}
                    openDetails={openDetails}
                    openRename={openRename}
                    openPreview={openPreview}
                  />
                </Fragment>
              ) : (
                <CollapsedGroupRows
                  key={group.documentType}
                  orgId={orgId}
                  group={group}
                  openDetails={openDetails}
                  openRename={openRename}
                  openPreview={openPreview}
                />
              )
            )}
          </TableBody>
        </Table>
      </div>
      {dialogs}
    </>
  )
}
